/**
 * Pure helpers for the task-drawer bound-data zones (§1.6 Build 4b-2).
 * Framework-free so they're unit-testable independently of the components.
 */
import { formatMoneyToRead } from "@shared/currency";
import type { ResolvedBinding, ResolvedCell } from "@shared/task-bindings";
import { SECTION_SLUGS as S } from "@shared/constants/sections";

// ── Per-record state, derived from the append-only event stream ────────────────

export interface TaskEventLite {
  eventType: string;
  recordRowId?: string | null;
  note?: string | null;
  payload?: unknown;
  createdAt?: string | null;
}

export interface RecordState {
  done: boolean;
  note: string | null;
  at: string | null; // ISO timestamp of the winning event
}

/**
 * Latest `record_action` event per rowId wins (append-only; undo is a later 'undone'
 * event, never a delete). Events arrive oldest-first from /events, so a forward scan
 * leaves the most recent state per row. Returns Map<rowId, RecordState>.
 */
export function deriveRecordStates(events: TaskEventLite[]): Map<string, RecordState> {
  const out = new Map<string, RecordState>();
  for (const e of events) {
    if (e.eventType !== "record_action" || !e.recordRowId) continue;
    const action = (e.payload as { action?: string } | null)?.action;
    out.set(e.recordRowId, {
      done: action === "done",
      note: e.note ?? null,
      at: e.createdAt ?? null,
    });
  }
  return out;
}

// ── Per-record LETTER state (§1.6 Build 4c) ────────────────────────────────────

export interface LetterState { generated: boolean; at: string | null; letterId: string | null }

/**
 * Latest `letter_generated` event per **instanceKey** (from the event payload, NOT the
 * recordRowId) → generated state. Keying on instanceKey is the point (D-124 ruling 2): all
 * rows sharing an instanceKey — e.g. multiple accounts at one bank — reflect the same single
 * letter and flip together, while per-policy insurance stays independent (its key has the
 * policy #). The drawer looks up a row's state by that row's `letterInstanceKey`.
 */
export function deriveLetterStates(events: TaskEventLite[]): Map<string, LetterState> {
  const out = new Map<string, LetterState>();
  for (const e of events) {
    if (e.eventType !== "letter_generated") continue;
    const p = e.payload as { instanceKey?: string; letterId?: string } | null;
    if (!p?.instanceKey) continue;
    out.set(p.instanceKey, { generated: true, at: e.createdAt ?? null, letterId: p.letterId ?? null });
  }
  return out;
}

// Letter action label by template type (Reade s124: bank → "Notification letter",
// life insurance → "Claim letter"; everything else → "Notification letter").
const LETTER_ACTION_LABELS: Record<string, string> = {
  bank_notification: "Notification letter",
  investment_notification: "Notification letter",
  life_insurance_claim: "Claim letter",
  home_insurance_notification: "Notification letter",
  auto_insurance_notification: "Notification letter",
  other_insurance_notification: "Notification letter",
  employer_active_notification: "Notification letter",
  employer_retired_notification: "Notification letter",
  // s126 LETTERS-GROUP-A: clearer labels than the generic "Letter" fallback.
  credit_card_notification: "Notification letter",
  dod_valuation_request: "Valuation request",
};
export function letterActionLabel(templateType: string): string {
  return LETTER_ACTION_LABELS[templateType] ?? "Letter";
}

/** Download a generated letter PDF via the executor-accessible letters route, honouring the
 *  server's descriptive Content-Disposition filename (s126 P2 item 2). Shared by the per-record
 *  (RecordRow) and task-level (TaskLetterZone) letter buttons — one implementation. */
export async function downloadLetter(clientId: string, letterId: string): Promise<void> {
  const res = await fetch(`/api/clients/${clientId}/letters/${letterId}/download`, { credentials: "include" });
  if (!res.ok) return;
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") ?? "";
  const filename = /filename="?([^"]+?)"?(?:;|$)/i.exec(cd)?.[1] || "letter.pdf";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Progress fraction for a Class A binding: how many of its rows are checked done. */
export function recordProgress(rows: { rowId: string }[], states: Map<string, RecordState>): { done: number; total: number } {
  const total = rows.length;
  const done = rows.filter((r) => states.get(r.rowId)?.done).length;
  return { done, total };
}

// ── Copy: action verbs + section nouns ─────────────────────────────────────────

interface Verb { button: string; past: string }
const DEFAULT_VERB: Verb = { button: "Mark done", past: "done" };

// Per-task action verb for the sub-item button + progress fraction ("1 of 3 notified").
// Default "Mark done" / "done" where no verb fits the task (D-123.10).
const TASK_VERBS: Record<string, Verb> = {
  "notify-banks": { button: "Mark notified", past: "notified" },
  "notify-beneficiaries": { button: "Mark notified", past: "notified" },
  "notify-employer": { button: "Mark notified", past: "notified" },
  "file-insurance-claims": { button: "Mark filed", past: "filed" },
  "cancel-credit-cards": { button: "Mark cancelled", past: "cancelled" },
  "cancel-subscriptions": { button: "Mark cancelled", past: "cancelled" },
  "cancel-licence-plates": { button: "Mark cancelled", past: "cancelled" },
  "contact-mortgage-lenders": { button: "Mark contacted", past: "contacted" },
  "manage-digital-estate": { button: "Mark handled", past: "handled" },
  "distribute-assets": { button: "Mark distributed", past: "distributed" },
  "obtain-releases": { button: "Mark obtained", past: "obtained" },
  "charity-residual-notification": { button: "Mark notified", past: "notified" },
  "secure-property": { button: "Mark secured", past: "secured" },
  "secure-pets": { button: "Mark arranged", past: "arranged" },
  "sell-transfer-assets": { button: "Mark transferred", past: "transferred" },
};

export function taskVerb(taskKey: string): Verb {
  return TASK_VERBS[taskKey] ?? DEFAULT_VERB;
}

// Plain-language noun per section for gap lines ("No bank accounts were recorded.").
const SECTION_NOUNS: Record<string, string> = {
  [S.FINANCIAL_BANK_ACCOUNTS]: "bank accounts",
  [S.FINANCIAL_INVESTMENTS_RRSP]: "investments",
  [S.FINANCIAL_AND_INSURANCE_INSURANCE_POLICIES]: "insurance policies",
  [S.FINANCIAL_DEBTS_LIABILITIES]: "debts",
  [S.FINANCIAL_AND_INSURANCE_PRE_AUTHORIZED_DEBITS]: "recurring services",
  [S.FINANCIAL_AND_INSURANCE_HOUSING]: "rentals",
  [S.FINANCIAL_BUSINESS_SELF_EMPLOYMENT]: "business interests",
  [S.ASSETS_PROPERTIES]: "properties",
  [S.ASSETS_VEHICLE_LIST]: "vehicles",
  [S.ASSETS_VALUABLES]: "valuables",
  beneficiaries: "beneficiaries", // no SECTION_SLUGS constant by design (documented exception)
  [S.LEGAL_IMPORTANT_DOCUMENTS]: "documents",
  [S.LEGAL_BUSINESS_DOCUMENTS]: "business documents",
  [S.LEGAL_BUSINESS_CONTACTS]: "business contacts",
  [S.PERSONAL_INFORMATION_CAREER]: "employers",
  [S.PERSONAL_INFORMATION_GOVERNMENT_IDS]: "government IDs",
  [S.PERSONAL_INFORMATION_HEALTH_MEDICAL]: "medical details",
  [S.INSTRUCTIONS_FOR_EXECUTORS_FUNERAL_WISHES]: "funeral wishes",
};

// s126 item 5: curated plain-language noun wins; otherwise fall back to the section's
// real DISPLAY name (carried on the resolved binding), never a de-slugified slug. The
// slug de-slugify remains only as a last resort when no display name is available.
export function sectionNoun(slug: string, sectionName?: string): string {
  return SECTION_NOUNS[slug] ?? sectionName ?? slug.replace(/-/g, " ");
}

// ── Cell / deep-link helpers ───────────────────────────────────────────────────

/** Deep link to the read-only plan view, section-level (D-123.13). Null category → no link. */
export function planDeepLink(categorySlug: string | null, section: string): string | null {
  if (!categorySlug) return null;
  return `/category/${categorySlug}?section=${encodeURIComponent(section)}`;
}

/** The identity cell (first) + the detail cells (rest that carry a value or a Cat A pointer). */
/** Split a record's cells into the row TITLE (identity) and the detail line.
 *
 *  s162 (HANDOVER-ROW-BUGS-1): the identity is the cell the SERVER marked (`identity: true`,
 *  from `identityKeyFor` = the curated RECORD_FIELD_BY_SECTION entry, else the binding's first
 *  field). This used to take `cells[0]` unconditionally, which silently ignored the curated
 *  identity whenever it wasn't the first DECLARED field — so a section's row title depended on
 *  task-authoring order, and four sibling children all titled by their shared `surname`.
 *  Falls back to `cells[0]` for any payload without the marker (older cached responses). */
export function splitCells(cells: ResolvedCell[]): { identity: ResolvedCell | null; detail: ResolvedCell[] } {
  if (cells.length === 0) return { identity: null, detail: [] };
  const identity = cells.find((c) => c.identity) ?? cells[0];
  const detail = cells
    // s162 (RECORD-IDENTITY-DISPLAY-1): drop the identity AND its component parts. On a two-part
    // person identity the title already reads "Avery Barber"; without this the surname repeated
    // immediately below it, sitting next to the guardian's name, which read as a second person.
    .filter((c) => c !== identity && !c.identityPart)
    .filter((c) => c.secureFilePointer || (c.value != null && c.value !== ""));
  return { identity, detail };
}

/** s232 D-232.79 (3): the ONE way the drawer prints a bound cell's value — money as money ("$100,000", cents only
 *  when not zero), everything else as it came. */
export function cellText(c: Pick<ResolvedCell, "value" | "fieldType">): string | null | undefined {
  return c.fieldType === "currency" && c.value ? formatMoneyToRead(c.value) : c.value;
}

export type { ResolvedBinding, ResolvedCell };
